/**
 * 金币/钻石收取飞行动画（纯 DOM + Web Animations API，不依赖业务）
 *
 * 用法：
 *   flyCoins({ from: bigDiamondEl, to: headerGemEl, icon: '/images/xxx.png', onComplete })
 *
 * 效果：从 from 位置散出 count 颗图标，各自沿随机弧线（二次贝塞尔）飞向 to，
 * 飞行中逐渐缩小，末段淡出；每颗到达时触发 onArrive 并对目标做一次脉冲放大。
 */

interface Point { x: number; y: number }
type Anchor = HTMLElement | Point

export interface CoinFlyOptions {
  /** 起点：元素（取中心）或坐标 */
  from: Anchor
  /** 终点：元素（取中心）或坐标 */
  to: Anchor
  /** 飞行图标地址 */
  icon: string
  /** 粒子数量，默认 8 */
  count?: number
  /** 单颗飞行时长 ms，默认 900 */
  duration?: number
  /** 相邻粒子起飞间隔 ms，默认 70 */
  stagger?: number
  /** 粒子尺寸 px，默认 30 */
  size?: number
  /** 起点散开半径 px，默认 6（所有粒子沿同一条弧线鱼贯飞行，只做轻微错位） */
  spread?: number
  /** 弧线弯曲幅度 px（相对起终点连线的垂直偏移），默认 40；0 为直线，正负决定向哪一侧鼓 */
  curve?: number
  /** 目标脉冲放大元素，默认与 to 相同（to 为元素时） */
  pulseTarget?: HTMLElement | null
  /** 单颗到达回调 */
  onArrive?: (index: number) => void
  /** 全部到达回调 */
  onComplete?: () => void
}

const Z_INDEX = 99999

function centerOf(anchor: Anchor): Point {
  if (anchor instanceof HTMLElement) {
    const r = anchor.getBoundingClientRect()
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
  }
  return anchor
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min)
}

/** 二次贝塞尔插值 */
function quadBezier(p0: Point, c: Point, p1: Point, t: number): Point {
  const mt = 1 - t
  return {
    x: mt * mt * p0.x + 2 * mt * t * c.x + t * t * p1.x,
    y: mt * mt * p0.y + 2 * mt * t * c.y + t * t * p1.y,
  }
}

/** 把一条弧线采样成 WAAPI keyframes（transform + opacity） */
function buildKeyframes(start: Point, ctrl: Point, end: Point, size: number, steps = 24): Keyframe[] {
  const frames: Keyframe[] = []
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const p = quadBezier(start, ctrl, end, t)
    const scale = 1 - 0.6 * t // 1 -> 0.4
    const opacity = t < 0.85 ? 1 : 1 - (t - 0.85) / 0.15
    frames.push({
      offset: t,
      transform: `translate(${p.x - size / 2}px, ${p.y - size / 2}px) scale(${scale})`,
      opacity,
    })
  }
  return frames
}

function createParticle(icon: string, size: number): HTMLImageElement {
  const img = document.createElement('img')
  img.src = icon
  img.alt = ''
  Object.assign(img.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${size}px`,
    height: `${size}px`,
    objectFit: 'contain',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
    zIndex: String(Z_INDEX),
  } as Partial<CSSStyleDeclaration>)
  return img
}

function pulse(el: HTMLElement | null | undefined) {
  if (!el || typeof el.animate !== 'function')
    return
  el.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(1.4)' },
      { transform: 'scale(1)' },
    ],
    { duration: 260, easing: 'ease-out' },
  )
}

/**
 * 播放飞行动画，返回 Promise，全部粒子到达后 resolve。
 * 不支持 Web Animations API 的环境直接 resolve（降级为无动画）。
 */
export function flyCoins(options: CoinFlyOptions): Promise<void> {
  const {
    from,
    to,
    icon,
    count = 8,
    duration = 900,
    stagger = 70,
    size = 30,
    spread = 6,
    curve = 40,
    onArrive,
    onComplete,
  } = options

  const pulseTarget = options.pulseTarget ?? (to instanceof HTMLElement ? to : null)

  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof Element.prototype.animate !== 'function') {
      onComplete?.()
      resolve()
      return
    }

    const start = centerOf(from)
    const end = centerOf(to)
    // 所有粒子共用一条弧线：控制点 = 连线中点沿法线方向偏移 curve，形成略弯的近直线
    const mid: Point = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }
    const dx = end.x - start.x
    const dy = end.y - start.y
    const len = Math.hypot(dx, dy) || 1
    const ctrl: Point = { x: mid.x + (-dy / len) * curve, y: mid.y + (dx / len) * curve }
    const container = document.createDocumentFragment()
    let arrived = 0

    for (let i = 0; i < count; i++) {
      // 起点只做轻微随机错位，靠 stagger 形成沿线鱼贯而上的效果
      const p0: Point = { x: start.x + rand(-spread, spread), y: start.y + rand(-spread, spread) }

      const el = createParticle(icon, size)
      // delay 期间动画不生效（fill: forwards 不回填），用基础样式藏起来，避免闪现在 (0,0)
      el.style.opacity = '0'
      container.appendChild(el)

      const anim = el.animate(buildKeyframes(p0, ctrl, end, size), {
        duration,
        delay: i * stagger,
        easing: 'cubic-bezier(0.45, 0.05, 0.55, 0.95)',
        fill: 'forwards',
      })
      anim.onfinish = () => {
        el.remove()
        pulse(pulseTarget)
        onArrive?.(i)
        arrived++
        if (arrived === count) {
          onComplete?.()
          resolve()
        }
      }
    }

    document.body.appendChild(container)
  })
}
